import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Switch, useHistory, useParams } from 'react-router-dom';
import { cloneDeep } from 'lodash';
import { v4 as uuid } from 'uuid';
import PrivateRoute from "@common/components/route/PrivateRoute";
import { Typography, Box } from "@curves/ui-base";
import useDocMembershipApi from '@apis/useDocMembershipApi';
import useToggleDialog from '@util/hook/useToggleDialog';
import { PEOPLE_TYPE } from '@icoach/constants/dataEnums';
import { CLUB_DOCUMENTS_ROUTER } from '@icoach/router/routerPath';
import MembershipCreateContent from "@icoach/documents/membership/create/MembershipCreateContent";
import MembershipCreateHistory from '@icoach/documents/membership/create/MembershipCreateHistory';
import { DOC_MEMBERSHIP_LINK } from '@icoach/documents/membership/staticData';
import { BlankFormBox, SearchBar } from '@icoach/documents/components/';
import { ConfirmTemporaryContractDialog, ConfirmHealthDeclarationNotifyDialog } from '@icoach/documents/dialog/';
import useDocHealthDeclarationApi from '@apis/useDocHealthDeclarationApi';
import { QUERY_KEYS } from '@icoach/constants/queryConstants';
import { isEmpty, isEqual } from '@util/utils';
import { shouldShowHealthDeclarationDialog } from '@icoach/documents/membership/create/membershipFormRules';

const dialogTypeMap = {
    temporary: 'temporary',
    healthDeclarationNotify: 'healthDeclarationNotify',
};

const MembershipCreatePage = ({ location }) => {
    const { isApplySuccess } = location.state || {};
    const history = useHistory();
    const { peopleType: peopleTypeProps, referenceID: referenceIDProps } = useParams();
    const { getDocMembershipOptionApi, getDocMembershipTemporaryApi, deleteDocMembershipTemporaryApi } = useDocMembershipApi();
    const { getDocHealthDeclarationIsValidApi } = useDocHealthDeclarationApi();
    const { openTarget, handleOpenDialog, handleCloseDialog } = useToggleDialog();
    const [isMounted, setMounted] = useState(false);
    const [options, setOptions] = useState({});
    const searchRef = useRef([]);
    const temporaryIDRef = useRef(0);

    // 前往新增/編輯合約頁
    const goToContractPage = (target) => {
        const { peopleType = peopleTypeProps, referenceID = referenceIDProps, contractID } = target;
        const path = CLUB_DOCUMENTS_ROUTER.MEMBERSHIP.GET_CONTRACT_PATH(peopleType, referenceID, contractID);
        history.push(path);
    };

    const goToHealthDeclarationPage = (target) => {
        const { peopleType = peopleTypeProps, referenceID = referenceIDProps } = target;
        let path = CLUB_DOCUMENTS_ROUTER.HEALTHDECLARATION.GET_CONTRACT_PATH(peopleType, referenceID, 0);
        path = path + `?${QUERY_KEYS.CONTINUE_DOC}`;
        history.push(path);
    };

    const checkPeopleType = (opts) => {
        const { targetOptions = [] } = opts;
        const isInclude = targetOptions.find(({ value }) => isEqual(peopleTypeProps, value));
        const defaultPeopleType = PEOPLE_TYPE.CUSTOMER;
        if (!peopleTypeProps || !isInclude) {
            history.replace(`${CLUB_DOCUMENTS_ROUTER.MEMBERSHIP.BASE}${defaultPeopleType}`);
        }
    };

    // 文書重設 按鈕
    const handleResetClick = (type) => {
        const path = `${CLUB_DOCUMENTS_ROUTER.MEMBERSHIP.BASE}${type}`;
        history.replace(path);
    };

    // 搜尋對象/返回 按鈕
    const handleSearchHistoryClick = (id, isMember) => {
        const { targetID, targetType } = searchRef.current.getResult();
        const refID = id || targetID;
        let refType = isMember ? 2 : targetType;
        const path = CLUB_DOCUMENTS_ROUTER.MEMBERSHIP.GET_HISTORY_PATH(refType, refID);
        if (isMember) {
            history.replace(path);
            setOptions((prev) => cloneDeep(prev));
        } else {
            history.replace(path);
        }
    };

    // 新增文書 按鈕
    const handleCreateClick = async () => {
        const { targetID: id, targetType: type } = searchRef.current.getResult();
        const params = { type, id };

        const isValid = await doCheckHealthDeclarationApi(type, id);
        if (shouldShowHealthDeclarationDialog({ type, id, isValid })) {
            // 前置檢查健康申告書
            handleOpenDialog(dialogTypeMap.healthDeclarationNotify);
        } else {
            // 已有健康申告書
            if (!isEqual(type, PEOPLE_TYPE.ONLYJOIN)) {
                doCreateBlankContractApi(params, type, id);
            } else {
                goToContractPage({
                    peopleType: type,
                    referenceID: id,
                    contractID: 0,
                });
            }
        }
    };

    // 彈出視窗 要刪除暫存資料
    const handleDialogNoClick = () => {
        const { targetID: id, targetType: type } = searchRef.current.getResult();
        doDeleteTemporaryDataApi(type, id);
    };

    // 彈出視窗 讀取上次暫存資料繼續編輯
    const handleDialogYseClick = () => {
        if (!temporaryIDRef.current) return;
        handleCloseDialog();
        goToContractPage({ contractID: temporaryIDRef.current });
    };

    // 彈出視窗 跳至健康申告書
    const handleGoToHealthDeclarationPage = () => {
        const { targetID, targetType: peopleType } = searchRef.current.getResult();
        const referenceID = isEmpty(targetID) ? 0 : targetID;
        goToHealthDeclarationPage({
            peopleType,
            referenceID,
        });
    };

    // 檢查健康申告書
    const doCheckHealthDeclarationApi = async (type, id) => {
        return id > 0 && (await getDocHealthDeclarationIsValidApi({ type, id }));
    };

    // 刪除暫存合約資料
    const doDeleteTemporaryDataApi = async (type, id) => {
        const resp = await deleteDocMembershipTemporaryApi({ type, id });
        if (resp) {
            handleCloseDialog();
            goToContractPage({ contractID: 0 });
        }
    };

    // 新增空白頁面
    const doCreateBlankContractApi = async (params, type, id) => {
        let temporaryID;
        temporaryIDRef.current = 0;
        temporaryID = await getDocMembershipTemporaryApi(params);

        if (temporaryID !== 0 && !!temporaryID) {
            temporaryIDRef.current = temporaryID;
            handleOpenDialog(dialogTypeMap.temporary);
        } else {
            goToContractPage({
                peopleType: type,
                referenceID: id,
                contractID: 0,
            });
        }
    };

    // 初始 search options
    const doInitOptionsApi = async () => {
        const resp = await getDocMembershipOptionApi();
        if (resp) {
            checkPeopleType(resp);
            setOptions(resp);
        }
        setMounted(true);
    };

    const useSearchBarMemo = useMemo(
        () => {
            const key = uuid();
            const { targetOptions } = options;
            return (
                <SearchBar
                    key={key}
                    ref={searchRef}
                    className="mb-4"
                    sourceOption={targetOptions}
                    onResetEvent={handleResetClick}
                    onCreateEvent={handleCreateClick}
                    onSearchHistoryEvent={handleSearchHistoryClick}
                    routerPath={CLUB_DOCUMENTS_ROUTER.MEMBERSHIP.BASE}
                />
            );
        },
        // eslint-disable-next-line
        [options]
    );

    useEffect(
        () => {
            doInitOptionsApi();
            return () => setMounted(false);
        },
        // eslint-disable-next-line
        []
    );

    if (!isMounted) return null;

    return (
        <Box className="container main-container-spacing">
            <Box component="header" className="page-header">
                <Typography className="page-header-title" variant="h3">
                    入會申請書
                </Typography>
                <Box className="btn-group">
                    <BlankFormBox sourceData={DOC_MEMBERSHIP_LINK} />
                </Box>
            </Box>
            {useSearchBarMemo}
            <Switch>
                <PrivateRoute exact path={CLUB_DOCUMENTS_ROUTER.MEMBERSHIP.HISTORY} as={MembershipCreateHistory} onBlankContractClick={handleCreateClick} />
                <PrivateRoute exact path={CLUB_DOCUMENTS_ROUTER.MEMBERSHIP.CONTRACT} as={MembershipCreateContent} onReturnHistory={handleSearchHistoryClick} />
                <PrivateRoute
                    exact
                    path={CLUB_DOCUMENTS_ROUTER.MEMBERSHIP.READONLY_CONTRACT}
                    as={MembershipCreateContent}
                    isReadOnly={true}
                    onReturnHistory={handleSearchHistoryClick}
                    isApplySuccess={isApplySuccess || false}
                />
            </Switch>
            <ConfirmTemporaryContractDialog
                open={isEqual(openTarget, dialogTypeMap.temporary)}
                handleClose={handleCloseDialog}
                handleClickYes={handleDialogYseClick}
                handleClickNo={handleDialogNoClick}
            />
            <ConfirmHealthDeclarationNotifyDialog
                open={isEqual(openTarget, dialogTypeMap.healthDeclarationNotify)}
                handleClose={handleCloseDialog}
                handleClickYes={handleGoToHealthDeclarationPage}
                handleClickNo={handleCloseDialog}
            />
        </Box>
    );
};

export default MembershipCreatePage;
